import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { getSessionId, track } from './client';
import { toEndpointTemplate } from './endpoint';
import { isAxiosCancel } from './errors';
import { safely } from './safely';

export const SESSION_HEADER = 'x-telemetry-session';

// Stamped on the request config so the response side can time the call.
type TimedConfig = InternalAxiosRequestConfig & { telemetryStartedAt?: number };

export const onRequest = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  const timed = config as TimedConfig;
  safely(() => {
    timed.telemetryStartedAt = Date.now();
    if (timed.headers) timed.headers[SESSION_HEADER] = getSessionId();
  });
  return config;
};

const report = (
  config: InternalAxiosRequestConfig | undefined,
  status: number,
  kind: 'http' | 'network',
) => {
  const startedAt = (config as TimedConfig | undefined)?.telemetryStartedAt;
  track('api_error', {
    endpoint: toEndpointTemplate(config?.url ?? ''),
    method: (config?.method ?? 'get').toUpperCase(),
    status,
    durationMs: startedAt ? Date.now() - startedAt : 0,
    kind,
  });
};

// The instance has `validateStatus: () => true`, so 4xx/5xx arrive here as responses.
export const onResponse = (response: AxiosResponse): AxiosResponse => {
  safely(() => {
    if (response.status >= 400)
      report(response.config, response.status, 'http');
  });
  return response;
};

// Only network failures and timeouts reject; a cancelled request was asked for.
export const onResponseError = (error: unknown): Promise<never> => {
  safely(() => {
    if (isAxiosCancel(error)) return;
    const { config, response } = (error ?? {}) as AxiosError;
    report(config, response?.status ?? 0, response ? 'http' : 'network');
  });
  return Promise.reject(error);
};

/** One request and one response interceptor; call once, when the instance is created. */
export function attachApiTelemetry(api: AxiosInstance): void {
  api.interceptors.request.use(onRequest);
  api.interceptors.response.use(onResponse, onResponseError);
}
